/**
 * Discovery contexts — CHIMERA body signals plus practical viewing contexts.
 *
 * Shared substrate-source consumed by both the Discover page (chat-style
 * AI discovery) and the DiscoveryPortal on Home (the substrate-anchor
 * surface immediately below the hero). Single source of truth so the two
 * surfaces never drift.
 */

export interface DiscoveryContext {
  id: string;
  icon: string;
  label: string;
  description: string;
  prompt: string;
}

export const DISCOVERY_CONTEXTS: DiscoveryContext[] = [
  {
    id: 'restore_rhythm',
    icon: '〰️',
    label: 'Restore Rhythm',
    description: 'When life feels scattered',
    prompt: 'movies and shows that restore rhythm: grounded, emotionally regulating, hopeful without being fake, characters rebuilding a life or team after disorder.',
  },
  {
    id: 'chosen_family',
    icon: '🤝',
    label: 'Chosen Family',
    description: 'Many bodies, one body',
    prompt: 'stories about chosen family, crews, teams, found family, many different people becoming one body without losing difference. Include anime, superhero, adventure, and heartfelt ensemble stories.',
  },
  {
    id: 'break_loop',
    icon: '⟲',
    label: 'Break the Loop',
    description: 'Pattern-breaking stories',
    prompt: 'stories about breaking cycles, escaping false systems, outliers revealing hidden rules, characters refusing the role assigned to them.',
  },
  {
    id: 'wonder',
    icon: '✨',
    label: 'Wonder Field Trip',
    description: 'Magic School Bus energy',
    prompt: 'wonder-filled science, adventure, fantasy, anime, and family-friendly stories that feel like a field trip into another world while still teaching something human.',
  },
  {
    id: 'date_night',
    icon: '💕',
    label: 'Date Night',
    description: 'Connection, laughs, reality TV',
    prompt: 'reality TV shows, dating shows, competition shows, romantic comedies, and emotionally easy couple watches. Prioritize: Love is Blind, The Circle, Love Island, Beast Games, Squid Game Challenge style shows.',
  },
  {
    id: 'solo',
    icon: '🎮',
    label: 'Solo Power',
    description: 'Anime, heroes, sci-fi',
    prompt: 'epic anime like One Piece, superhero stories, action movies, sci-fi, mind-bending films like Inception or Interstellar, Star Wars universe content.',
  },
  {
    id: 'nostalgic',
    icon: '📼',
    label: 'Nostalgic',
    description: '1995-2010 classics',
    prompt: 'classic content from 1995-2010 era, Disney classics, Cartoon Network shows, Nickelodeon shows, early MTV, classic sitcoms from that era.',
  },
  {
    id: 'trending',
    icon: '🔥',
    label: 'Trending Now',
    description: 'What society is watching',
    prompt: 'currently trending and viral TV shows and movies that reveal what society is processing right now: memes, discourse, box office, streaming charts, and cultural momentum.',
  },
];

export function findDiscoveryContext(id: string | null | undefined): DiscoveryContext | undefined {
  if (!id) return undefined;
  return DISCOVERY_CONTEXTS.find((context) => context.id === id);
}
